/**
 * Estado de la «columna de conversación de zona» (GEN-EKB-022b): qué chat lleva, si está abierta,
 * el mensaje que toca enviar y si el turno está en curso. Lo comparten todas las zonas que presiden un
 * agente: Saber con Jimmy hoy; administración de usuarios, GRs… cuando les toque.
 *
 * El cuadro de escritura NO vive aquí: lo pone la zona. Este hook solo decide a qué conversación va.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { leerChatDeZona, guardarChatDeZona, olvidarChatDeZona } from '../lib/conversacionZona.js'

const TITULO_NUEVA = 'Nueva conversación'

const borradorDe = (agente) => ({
  chat_id: `c-${crypto.randomUUID()}`,
  titulo: TITULO_NUEVA,
  de_que_va: '',
  agentes_en_chat: [{ nombre: agente.nombre, proposito: 'consulta directa' }],
  agente_destino: agente.id,
  agente_nombre: agente.nombre,
  iniciado: false,
  updated_at: new Date().toISOString(),
})

export function useConversacionZona({ zona, agente, usuario, activa, chats = [] }) {
  const [chat, setChat] = useState(null)       // chat de la columna (borrador o retomado)
  const [abierta, setAbierta] = useState(false)
  const [envio, setEnvio] = useState(null)     // { texto, n }
  const [ocupadaChat, setOcupada] = useState(false)
  const cerradaPorUsuario = useRef(false)
  const chatRef = useRef(null)
  chatRef.current = chat
  const agenteId = agente?.id ?? null

  // Al entrar en la zona se retoma la última conversación guardada (si no se cerró la columna a mano).
  useEffect(() => {
    if (!activa || !agente) return
    if (chatRef.current) { if (!cerradaPorUsuario.current) setAbierta(true); return }
    const id = leerChatDeZona(zona, usuario)
    if (!id) return
    setChat({ ...borradorDe(agente), chat_id: id, iniciado: true, titulo: 'Conversación con ' + agente.nombre })
    setAbierta(true)
  }, [activa, agenteId, zona, usuario])  // eslint-disable-line react-hooks/exhaustive-deps

  const visible = !!(activa && agente && abierta && chat)

  const asegurarChat = useCallback(() => {
    if (chatRef.current) return chatRef.current
    const c = borradorDe(agente)
    chatRef.current = c
    setChat(c)
    return c
  }, [agente])

  const abrir = useCallback(() => {
    if (!agente) return
    asegurarChat()
    cerradaPorUsuario.current = false
    setAbierta(true)
  }, [agente, asegurarChat])

  const enviar = useCallback((texto) => {
    abrir()
    setEnvio({ texto, n: Date.now() })
  }, [abrir])

  // El envío pendiente se borra en cuanto la ventana lo manda: si no, al remontarse la columna se reenviaría.
  const consumirEnvio = useCallback((n) => setEnvio(e => (e && e.n === n ? null : e)), [])

  const cerrar = useCallback(() => { cerradaPorUsuario.current = true; setAbierta(false) }, [])

  const nueva = useCallback(() => {
    if (!agente) return
    olvidarChatDeZona(zona, usuario)
    const c = borradorDe(agente)
    chatRef.current = c
    setChat(c)
    setEnvio(null)
    setOcupada(false)
  }, [agente, zona, usuario])

  // El primer mensaje de un borrador fija el título y deja guardado el chat para retomarlo.
  const alPrimerEnvio = useCallback((contenido) => {
    const c = chatRef.current
    if (!c) return
    guardarChatDeZona(zona, usuario, c.chat_id)
    const titulo = contenido.length > 48 ? `${contenido.slice(0, 48)}…` : contenido
    setChat(prev => (prev && prev.chat_id === c.chat_id ? { ...prev, titulo, iniciado: true } : prev))
  }, [zona, usuario])

  // Metadatos para ChatWindow: lo que dice el kernel manda; el destino es siempre el agente de la zona.
  const delKernel = chat ? chats.find(c => c.chat_id === chat.chat_id) : null
  const chatMeta = chat && agente
    ? { ...chat, ...(delKernel ?? {}), agente_destino: delKernel?.agente_destino ?? agente.id, agente_nombre: agente.nombre }
    : null
  const esBorrador = !!chat && !delKernel && !chat.iniciado

  return {
    visible, chat: chatMeta, esBorrador, envio,
    ocupada: visible && ocupadaChat,
    // «Nueva conversación» del cuadro (022c): activa solo si hay algo que dejar atrás y el agente no está respondiendo.
    puedeNueva: !!chat && !esBorrador && !ocupadaChat,
    abrir, enviar, consumirEnvio, cerrar, nueva, alPrimerEnvio, setOcupada,
  }
}
